import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Form, Input, Modal, Select, Space, Tag, Typography, message } from "antd";

import { AllocationSetupContext } from "../../context/AllocationSetupContext";

const { Text } = Typography;

const errorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) {
    return fallback;
  }
  if (typeof data.detail === "string") {
    return data.detail;
  }
  if (Array.isArray(data.name) && data.name.length) {
    return data.name[0];
  }
  if (Array.isArray(data.non_field_errors) && data.non_field_errors.length) {
    return data.non_field_errors[0];
  }
  return fallback;
};

const AllocationSetupSwitcher = () => {
  const {
    setups,
    activeSetup,
    viewedSetup,
    loading,
    canActivateSetups,
    canManageSetups,
    selectViewedSetup,
    createSetup,
    updateSetup,
    deleteSetup,
    activateSetup,
    lockSetup,
    getReopenPreview,
    reopenSetup,
    refreshSetups,
    viewedSetupMissingNotice,
    clearViewedSetupMissingNotice,
  } = useContext(AllocationSetupContext);

  const [createBlankModalOpen, setCreateBlankModalOpen] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  /** Display-only label for Rename modal header (targets ref-driven id snapshot). */
  const [renameModalSetupLabel, setRenameModalSetupLabel] = useState("");
  const [setupBeingDuplicated, setSetupBeingDuplicated] = useState(null);
  /** Snapshot of setup at Rename click — same pattern as Delete (target captured when the button fires). */
  const renameTargetRef = useRef(null);
  const [createBlankForm] = Form.useForm();
  const [duplicateForm] = Form.useForm();
  const [renameForm] = Form.useForm();

  const visibleSetups = setups;
  const draftSetups = useMemo(() => visibleSetups.filter((setup) => setup.status !== "archived"), [visibleSetups]);
  const lockedSetups = useMemo(() => visibleSetups.filter((setup) => setup.status === "archived"), [visibleSetups]);

  const selectedSetup =
    viewedSetup ||
    visibleSetups.find((setup) => setup.id === viewedSetup?.id) ||
    activeSetup ||
    visibleSetups[0] ||
    null;
  const selectedSetupLocked = selectedSetup?.status === "archived";

  useEffect(() => {
    if (duplicateModalOpen && setupBeingDuplicated) {
      duplicateForm.setFieldsValue({
        name: `${setupBeingDuplicated.name} Copy`,
        description: setupBeingDuplicated.description,
      });
    }
  }, [duplicateForm, duplicateModalOpen, setupBeingDuplicated]);

  if (!canManageSetups) {
    return null;
  }

  const handleCreate = async () => {
    try {
      const values = await createBlankForm.validateFields();
      await createSetup({ ...values, creationMode: "blank" });
      message.success("Blank allocation setup created.");
      setCreateBlankModalOpen(false);
      createBlankForm.resetFields();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error(errorMessage(error, "Failed to create allocation setup."));
    }
  };

  const handleDuplicate = async () => {
    if (!setupBeingDuplicated) {
      return;
    }

    try {
      const values = await duplicateForm.validateFields();
      await createSetup({
        ...values,
        creationMode: "clone",
        sourceSetupId: setupBeingDuplicated.id,
      });
      message.success("Allocation setup duplicated successfully.");
      setDuplicateModalOpen(false);
      setSetupBeingDuplicated(null);
      duplicateForm.resetFields();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error(errorMessage(error, "Failed to duplicate allocation setup."));
    }
  };

  const handleRename = async () => {
    const target = renameTargetRef.current;
    if (!target) {
      return;
    }

    try {
      const values = await renameForm.validateFields();
      await updateSetup(target.id, values);
      message.success("Allocation setup updated successfully.");
      setRenameModalOpen(false);
      renameTargetRef.current = null;
      setRenameModalSetupLabel("");
      renameForm.resetFields();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error(errorMessage(error, "Failed to update allocation setup."));
    }
  };

  const handleDelete = () => {
    const setupBeingDeleted = selectedSetup;
    if (!setupBeingDeleted || setupBeingDeleted.is_active) {
      return;
    }

    Modal.confirm({
      title: `Delete ${setupBeingDeleted.name}?`,
      content: "This permanently removes the setup and its setup-scoped allocation data. This cannot be undone.",
      onOk: async () => {
        try {
          await deleteSetup(setupBeingDeleted.id);
          message.success("Allocation setup deleted.");
        } catch (error) {
          message.error(errorMessage(error, "Failed to delete allocation setup."));
        }
      },
    });
  };

  const handleActivate = async () => {
    if (!selectedSetup || selectedSetup.is_active || selectedSetupLocked) {
      return;
    }

    Modal.confirm({
      title: `Activate ${selectedSetup.name}?`,
      content:
        "This replaces the current live allocation graph for loaders, staff, walkway crews, and salting routes. Dispatch staging will be cleared and the planning pages will refresh.",
      onOk: async () => {
        try {
          await activateSetup(selectedSetup.id);
          message.success("Allocation setup activated.");
        } catch (error) {
          message.error(errorMessage(error, "Failed to activate allocation setup."));
        }
      },
    });
  };

  const handleLock = () => {
    if (!selectedSetup || selectedSetup.is_active || selectedSetupLocked || !lockSetup) return;
    Modal.confirm({
      title: `Lock ${selectedSetup.name}?`,
      content: "This makes the completed setup read-only. Future resource archives will preserve its allocations.",
      okText: "Lock Setup",
      onOk: async () => {
        try {
          await lockSetup(selectedSetup.id);
          message.success("Allocation setup locked.");
        } catch (error) {
          message.error(errorMessage(error, "Failed to lock allocation setup."));
        }
      },
    });
  };

  const handleReopen = async () => {
    if (!selectedSetupLocked || !getReopenPreview || !reopenSetup) return;
    try {
      const preview = await getReopenPreview(selectedSetup.id);
      const unavailable = Number(preview?.allocation_count || 0);
      let replacementName = selectedSetup.name;
      Modal.confirm({
        title: `Reopen ${selectedSetup.name}?`,
        content: (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text>
              {unavailable
                ? `Reopening will remove ${unavailable} allocation records that reference unavailable resources or incompatible staff positions.`
                : "This returns the setup to editable draft planning."}
            </Text>
            {(preview.incompatible_staff_assignments || []).length > 0 && <ul>
              {preview.incompatible_staff_assignments.map(row => <li key={`${row.kind}-${row.assignment_id}`}>
                {row.staff_name}: {row.name} — {row.reason}
              </li>)}
            </ul>}
            {preview?.name_conflict && (
              <>
                <Text>This historical name is already used by an editable setup. Enter a unique replacement name.</Text>
                <Input
                  defaultValue={selectedSetup.name}
                  aria-label="Replacement setup name"
                  onChange={(event) => { replacementName = event.target.value; }}
                />
              </>
            )}
          </Space>
        ),
        okText: "Reopen Setup",
        onOk: async () => {
          try {
            await reopenSetup(selectedSetup.id, {
              confirmed: unavailable > 0,
              expected_preview_hash: preview.preview_hash,
              ...(preview?.name_conflict ? { name: replacementName.trim() } : {}),
            });
            message.success("Allocation setup reopened.");
          } catch (error) {
            message.error(errorMessage(error, "Failed to reopen allocation setup."));
          }
        },
      });
    } catch (error) {
      message.error(errorMessage(error, "Failed to load reopen impact."));
    }
  };

  const openDuplicateModal = () => {
    if (!selectedSetup) {
      return;
    }
    setSetupBeingDuplicated(selectedSetup);
    setDuplicateModalOpen(true);
  };

  const openRenameModal = () => {
    const target = selectedSetup;
    if (!target) {
      return;
    }
    renameTargetRef.current = target;
    setRenameModalSetupLabel(target.name);
    renameForm.setFieldsValue({
      name: target.name,
      description: target.description,
    });
    setRenameModalOpen(true);
  };

  return (
    <>
      <Card
        size="small"
        loading={loading}
        style={{
          margin: "16px 20px 0",
          borderColor: "#d9eadf",
          boxShadow: "0 2px 8px rgba(3, 72, 50, 0.05)",
        }}
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
            <Space wrap>
              <Text strong>Allocation Setup</Text>
              {activeSetup ? <Tag color="green">Active: {activeSetup.name}</Tag> : <Tag>No active setup</Tag>}
              {selectedSetup ? (
                <Tag color={selectedSetup?.id === activeSetup?.id ? "green" : "blue"}>Viewed: {selectedSetup.name}</Tag>
              ) : null}
            </Space>
            <Space wrap>
              <Button onClick={() => setCreateBlankModalOpen(true)}>Create Blank Setup</Button>
              <Button onClick={openDuplicateModal} disabled={!selectedSetup || selectedSetupLocked}>
                Duplicate
              </Button>
              <Button onClick={openRenameModal} disabled={!selectedSetup || selectedSetupLocked}>
                Rename
              </Button>
              <Button danger onClick={handleDelete} disabled={!selectedSetup || selectedSetup?.is_active || selectedSetupLocked}>
                Delete
              </Button>
              <Button
                type="primary"
                onClick={handleActivate}
                disabled={!canActivateSetups || !selectedSetup || selectedSetup?.is_active || selectedSetupLocked}
              >
                {selectedSetup?.is_active ? "Active" : "Activate"}
              </Button>
              {selectedSetupLocked ? (
                <Button onClick={handleReopen}>Reopen Setup</Button>
              ) : (
                <Button onClick={handleLock} disabled={!selectedSetup || selectedSetup?.is_active}>Lock Setup</Button>
              )}
              <Button onClick={refreshSetups}>Refresh</Button>
            </Space>
          </Space>

          <Select
            value={selectedSetup?.id}
            onChange={selectViewedSetup}
            style={{ width: "100%" }}
            placeholder="Select an allocation setup"
            options={[
              {
                label: "Active and Draft Setups",
                options: draftSetups.map((setup) => ({
                  value: setup.id,
                  label: `${setup.name}${setup.is_active ? " (active)" : ""} | ${setup.walkway_crews} walkway | ${setup.salting_routes} salting | ${setup.finalized_loader_sites} finalized sites`,
                })),
              },
              ...(lockedSetups.length ? [{
                label: "Locked History",
                options: lockedSetups.map((setup) => ({
                  value: setup.id,
                  label: `${setup.name} (locked) | ${setup.walkway_crews} walkway | ${setup.salting_routes} salting | ${setup.finalized_loader_sites} finalized sites`,
                })),
              }] : []),
            ]}
          />

          {viewedSetupMissingNotice ? (
            <Alert
              type="warning"
              showIcon
              closable
              onClose={clearViewedSetupMissingNotice}
              message="The setup you were viewing is no longer available. Showing the active setup."
            />
          ) : null}

          <Alert
            type="info"
            showIcon
            message={
              selectedSetupLocked
                ? "Viewing locked historical setup"
                : selectedSetup?.id === activeSetup?.id ? "Editing active setup" : "Editing inactive setup"
            }
            description={
              selectedSetupLocked
                ? `Read-only. Locked by ${selectedSetup.locked_by_email || "an unknown manager"}${selectedSetup.locked_at ? ` on ${new Date(selectedSetup.locked_at).toLocaleString()}` : ""}. Current eligible resources are shown alongside preserved assignments. Archived resources remain visible in this history but are unavailable to editable plans.`
                : selectedSetup?.id === activeSetup?.id
                ? "Edits to this setup go live immediately. Operations, dispatch, and PDFs read this setup."
                : "Edits are saved as a draft. Activate to make this setup the live operational source."
            }
          />
        </Space>
      </Card>

      <Modal
        title="Create Blank Allocation Setup"
        open={createBlankModalOpen}
        onCancel={() => {
          setCreateBlankModalOpen(false);
          createBlankForm.resetFields();
        }}
        onOk={handleCreate}
      >
        <Form form={createBlankForm} layout="vertical">
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter a setup name." }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Duplicate ${setupBeingDuplicated?.name || "setup"}`}
        open={duplicateModalOpen}
        onCancel={() => {
          setDuplicateModalOpen(false);
          setSetupBeingDuplicated(null);
          duplicateForm.resetFields();
        }}
        onOk={handleDuplicate}
      >
        <Form form={duplicateForm} layout="vertical">
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter a setup name." }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Rename ${renameModalSetupLabel || "setup"}`}
        open={renameModalOpen}
        onCancel={() => {
          setRenameModalOpen(false);
          renameTargetRef.current = null;
          setRenameModalSetupLabel("");
          renameForm.resetFields();
        }}
        onOk={handleRename}
      >
        <Form
          form={renameForm}
          layout="vertical"
        >
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter a setup name." }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AllocationSetupSwitcher;

